import { Fragment, useState } from "react";
import dayjs from "dayjs";
import {
  Loader2,
  Check,
  Settings2,
  ChevronDown,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

import { formatDate } from "@/lib/format";
import { CORPORATE_PLAN_NAME } from "@/lib/domain-constants";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AdminUser, AdminOrganization } from "@/types/domain";

/** Días sin entrar. null = sin rastro de actividad. */
export function daysInactive(user: AdminUser): number | null {
  if (!user.lastSeenAt) return null;
  return dayjs().diff(dayjs(user.lastSeenAt), "day");
}

// Semáforo de actividad: la señal que se lee de un vistazo al escanear la tabla.
function activityTone(days: number | null) {
  if (days === null) return { dot: "bg-muted-foreground/40", text: "text-muted-foreground" };
  if (days <= 7) return { dot: "bg-emerald-500", text: "text-foreground" };
  if (days <= 30) return { dot: "bg-amber-500", text: "text-amber-700" };
  return { dot: "bg-red-500", text: "text-red-700" };
}

function ActivityCell({ user }: { user: AdminUser }) {
  const days = daysInactive(user);
  const tone = activityTone(days);

  const label =
    days === null
      ? "Sin rastro"
      : days === 0
        ? "Hoy"
        : days === 1
          ? "Ayer"
          : `Hace ${days} días`;

  return (
    <div className="flex flex-col gap-0.5">
      <span className={cn("flex items-center gap-2 text-sm font-medium", tone.text)}>
        <span className={cn("h-2 w-2 shrink-0 rounded-full", tone.dot)} />
        {label}
      </span>
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        Alta {formatDate(user.createdAt)}
      </span>
    </div>
  );
}

/**
 * Identidad: nombre, correo y las señales que importan al escanear — si está
 * pendiente de revisión, si es una cuenta de equipo (lo dice su PLAN, no cuánta
 * gente tenga) y de qué empresa, cuando la empresa no se llama igual que la
 * persona (en cuentas individuales siempre coinciden).
 */
function UserCell({
  user,
  organization,
}: {
  user: AdminUser;
  organization?: AdminOrganization;
}) {
  const isPending = user.betaformcompleted && !user.betaaccepted;
  const role = organization?.members.find((m) => m.userId === user.id)?.role;
  const isTeam = organization?.plan?.name === CORPORATE_PLAN_NAME;
  const showCompany = !!organization && organization.name !== user.name;

  return (
    <div className="flex min-w-[200px] flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-medium">{user.name}</span>
        {isPending && (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
            title="Pendiente de revisión"
          />
        )}
      </div>
      <span className="text-sm text-muted-foreground">{user.email}</span>
      {user.whatsapp &&
        // Los usuarios con username de WhatsApp nos ocultan su número: lo verificado
        // es un BSUID, que no es teléfono ni tiene wa.me al que enlazar.
        (/^\d+$/.test(user.whatsapp) ? (
          <a
            href={`https://wa.me/${user.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex w-fit items-center gap-1 text-xs font-medium text-emerald-600 hover:underline"
            title="Recibe alertas por WhatsApp"
          >
            <MessageCircle className="h-3 w-3" />+{user.whatsapp}
          </a>
        ) : (
          <span
            className="flex w-fit items-center gap-1 text-xs font-medium text-emerald-600"
            title="Recibe alertas por WhatsApp; el usuario mantiene su número oculto"
          >
            <MessageCircle className="h-3 w-3" />activo
          </span>
        ))}
      {(showCompany || isTeam) && (
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          {showCompany && (
            <Badge variant="outline" className="font-normal">
              {organization!.name}
            </Badge>
          )}
          {isTeam && (
            <Badge variant="secondary" className="font-normal">
              Equipo · {organization!.members.length}
              {role && role !== "member" ? ` · ${role}` : ""}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

/** Resumen de las líneas asignadas. El detalle con términos va en la fila abierta. */
function ProfilesCell({
  user,
  organization,
}: {
  user: AdminUser;
  organization?: AdminOrganization;
}) {
  const profiles =
    organization?.searchProfiles.filter((p) =>
      p.assignedUserIds.includes(user.id),
    ) ?? [];

  if (profiles.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const terms = profiles.reduce(
    (sum, p) => sum + p.keywords.length + p.interests.length,
    0,
  );

  return (
    <div className="flex max-w-[240px] flex-col gap-0.5">
      <span className="text-sm font-medium">
        {profiles.length} línea{profiles.length === 1 ? "" : "s"} ·{" "}
        <span className={terms === 0 ? "text-amber-600" : ""}>
          {terms} término{terms === 1 ? "" : "s"}
        </span>
      </span>
      <span className="line-clamp-2 text-xs text-muted-foreground">
        {profiles.map((p) => p.name).join(" · ")}
      </span>
    </div>
  );
}

function PlanBadge({ user }: { user: AdminUser }) {
  const sub = user.subscription;
  if (!sub) return <span className="text-muted-foreground">—</span>;

  // Un plan sin vencimiento no caduca, pero sus vistas pueden recargarse al
  // cambiar de mes: esa es la fecha que importa mostrar. Sin `resetsAt` el saldo
  // es de por vida y no hay fecha que dar.
  const footnote = sub.expiresat
    ? `Vence ${formatDate(sub.expiresat)}`
    : user.views.resetsAt
      ? `Renueva ${formatDate(user.views.resetsAt)}`
      : null;

  return (
    <div className="flex flex-col gap-0.5">
      <Badge variant={sub.isActive ? "default" : "secondary"} className="w-fit">
        {sub.planName}
        {!sub.isActive && " · vencido"}
      </Badge>
      {footnote && (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {footnote}
        </span>
      )}
    </div>
  );
}

function ViewsCell({ user }: { user: AdminUser }) {
  const { limit, remaining, unlocked } = user.views;
  // `limit` llega en null tanto con un plan ilimitado como sin plan vigente: la
  // suscripción es lo único que distingue "ve todo" de "no ve nada".
  const hasPlan = user.subscription?.isActive ?? false;
  const label = !hasPlan
    ? "—"
    : limit == null
      ? "Ilimitado"
      : `${remaining} / ${limit}`;

  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={
          hasPlan ? "text-sm font-medium" : "text-sm text-muted-foreground"
        }
      >
        {label}
      </span>
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        {unlocked} desbloqueadas
      </span>
    </div>
  );
}

/**
 * Fila abierta: todo lo que no cabe en la tabla sin romperla — el formulario de
 * beta y las líneas con TODOS sus términos. Nada recortado, porque acá el ancho
 * es la fila completa y los chips envuelven.
 */
function UserDetail({
  user,
  organization,
}: {
  user: AdminUser;
  organization?: AdminOrganization;
}) {
  const profiles =
    organization?.searchProfiles.filter((p) =>
      p.assignedUserIds.includes(user.id),
    ) ?? [];

  const hasRequest =
    user.workplace || user.mainfunction || user.contactnumber;

  return (
    <div className="grid gap-6 bg-muted/30 px-4 py-4 lg:grid-cols-[280px_1fr]">
      <section>
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Solicitud
        </h4>
        {hasRequest ? (
          <div className="flex flex-col gap-1 text-sm">
            {user.workplace && <span className="font-medium">{user.workplace}</span>}
            {user.mainfunction && (
              <span className="text-muted-foreground">{user.mainfunction}</span>
            )}
            {user.contactnumber && (
              <span className="text-muted-foreground">
                📞 {user.contactnumber}
              </span>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No envió el formulario de beta.
          </p>
        )}
      </section>

      <section>
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Líneas de negocio
        </h4>
        {profiles.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No tiene líneas asignadas: no ve ninguna oportunidad.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {profiles.map((profile) => {
              const terms = [...profile.keywords, ...profile.interests];
              return (
                <div key={profile.id}>
                  <p className="text-sm font-medium">{profile.name}</p>
                  {terms.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      Sin términos configurados.
                    </p>
                  ) : (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {profile.keywords.map((keyword) => (
                        <Badge
                          key={`k-${keyword}`}
                          variant="secondary"
                          className="font-normal"
                        >
                          {keyword}
                        </Badge>
                      ))}
                      {profile.interests.map((interest) => (
                        <Badge
                          key={`i-${interest}`}
                          variant="outline"
                          className="font-normal"
                        >
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export function AdminUsersTable({
  users,
  organizationsById,
  acceptingUserIds,
  onAccept,
  onManage,
}: {
  users: AdminUser[];
  organizationsById: Map<string, AdminOrganization>;
  acceptingUserIds: string[];
  onAccept: (user: AdminUser) => void;
  onManage: (user: AdminUser) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Usuario</TableHead>
            <TableHead>Actividad</TableHead>
            <TableHead>Líneas de negocio</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Vistas</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const isPending = user.betaformcompleted && !user.betaaccepted;
            const isAccepting = acceptingUserIds.includes(user.id);
            const isOpen = openId === user.id;
            const organization = user.organizationId
              ? organizationsById.get(user.organizationId)
              : undefined;

            return (
              <Fragment key={user.id}>
                <TableRow
                  className={cn("cursor-pointer", isOpen && "border-b-0")}
                  onClick={() => setOpenId(isOpen ? null : user.id)}
                >
                  <TableCell className="pr-0">
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    <UserCell user={user} organization={organization} />
                  </TableCell>
                  <TableCell>
                    <ActivityCell user={user} />
                  </TableCell>
                  <TableCell>
                    <ProfilesCell user={user} organization={organization} />
                  </TableCell>
                  <TableCell>
                    <PlanBadge user={user} />
                  </TableCell>
                  <TableCell>
                    <ViewsCell user={user} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      {isPending && (
                        <Button
                          size="sm"
                          disabled={isAccepting}
                          onClick={() => onAccept(user)}
                        >
                          {isAccepting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                          Aceptar trial
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onManage(user)}
                      >
                        <Settings2 className="h-4 w-4" />
                        Gestionar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

                {isOpen && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={7} className="p-0">
                      <UserDetail user={user} organization={organization} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
